// ----------------------------------------------------------------------------
// Song Studio building blocks shared by the admin page (/admin/chord-library)
// and the member page (/upload): library meter, upload banners, drop zone and
// the grouped song rows (with the remove confirmation).
// ----------------------------------------------------------------------------

import { useRef, useState, type DragEvent } from 'react';
import { AlertCircle, Check, Monitor, Upload, X } from 'lucide-react';
import type { ChordUpload } from '@app/api/chords';
import { CHORD_LIBRARY_SONG_LIMIT } from '@app/api/chords';
import { Button, ConfirmDialog, Meter, SectionLabel, Spinner, cx } from '@app/components/ui';
import { ChordUploadItem } from './ChordUploadItem';
import { STUDIO_ACCEPT, STUDIO_FORMAT_ERROR, isStudioAudio } from './studioFiles';

export const countSaved = (uploads: ChordUpload[]) => uploads.filter((u) => !u.sourceId.startsWith('temp-')).length;

export function LibraryMeter({ count, limit = CHORD_LIBRARY_SONG_LIMIT, unit = '', className }: { count: number; limit?: number; unit?: string; className?: string }) {
  const left = limit - count;
  const cap = count === 0 ? `${limit} songs available` : count >= limit ? 'Full. New uploads replace a song.' : `${left} ${left === 1 ? 'slot' : 'slots'} left`;
  return (
    <div className={cx('rounded-[18px] border border-line bg-card px-4 py-3.5', className)}>
      <div className="flex justify-between items-baseline">
        <span className="text-[12.5px] text-t4">Library</span>
        <span>
          <span className="font-mono text-[18px] font-bold">{count}</span>
          <span className="font-mono text-[13px] text-t4">
            {' '}
            / {limit}
            {unit}
          </span>
        </span>
      </div>
      <Meter value={count} limit={limit} className="mt-2.5" label="Song Studio library" />
      <div className="text-[12px] text-t3 mt-2">{cap}</div>
    </div>
  );
}

export type UploadStatus = 'idle' | 'uploading' | 'done' | 'error';

export function UploadBanner({
  status,
  title,
  onDismiss,
  onRetry,
  doneLine = 'Uploaded. You can safely sign out or leave.',
}: {
  status: UploadStatus;
  title?: string;
  onDismiss: () => void;
  onRetry?: () => void;
  doneLine?: string;
}) {
  if (status === 'idle') return null;
  if (status === 'uploading') {
    return (
      <div role="status" aria-live="polite" className="flex gap-3 items-start px-4 py-3.5 rounded-[16px] bg-info-bg border border-line2 mb-3.5 wp-rise">
        <Spinner size={18} tone="info" className="mt-px" />
        <div className="flex-1 min-w-0 text-[13.5px] leading-normal">
          <div className="font-semibold truncate">Uploading{title ? ` "${title}"` : ' your song'}…</div>
          <div className="text-t3">Keep this page open until the upload finishes.</div>
        </div>
      </div>
    );
  }
  if (status === 'done') {
    return (
      <div role="status" aria-live="polite" className="flex gap-3 items-start px-4 py-3.5 rounded-[16px] bg-ok-bg mb-3.5 wp-rise">
        <Check size={18} strokeWidth={2.4} className="text-ok shrink-0 mt-px" aria-hidden="true" />
        <div className="flex-1 text-[13.5px] leading-normal">
          <div className="font-semibold">{doneLine}</div>
          <div className="text-t2">We&apos;re analyzing the chords on our servers and will notify you when it&apos;s ready.</div>
        </div>
        <button type="button" onClick={onDismiss} aria-label="Dismiss" className="w-[26px] h-[26px] rounded-full text-t3 hover:text-t1 flex items-center justify-center">
          <X size={16} aria-hidden="true" />
        </button>
      </div>
    );
  }
  return (
    <div role="alert" className="flex flex-wrap gap-3 items-start px-4 py-3.5 rounded-[16px] bg-acc-bg mb-3.5 wp-rise">
      <AlertCircle size={18} strokeWidth={2.2} className="text-acc shrink-0 mt-px" aria-hidden="true" />
      <div className="flex-[1_1_220px] text-[13.5px] leading-normal">
        <div className="font-semibold">Upload didn&apos;t finish.</div>
        <div className="text-t2">Please check your connection and try uploading again.</div>
      </div>
      <div className="flex gap-1.5">
        {onRetry && (
          <Button variant="dark" size="sm" onClick={onRetry}>
            Try again
          </Button>
        )}
        <button type="button" onClick={onDismiss} aria-label="Dismiss" className="w-8 h-8 rounded-full text-t3 hover:text-t1 flex items-center justify-center">
          <X size={16} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

/**
 * Dashed drop zone with a real file input behind "Choose file from computer".
 * Rejects anything that isn't MP3, WAV or M4A before the modal opens.
 */
export function StudioDropZone({
  empty,
  atLimit,
  onFile,
  onReject,
  ctaVariant = 'primary',
}: {
  empty: boolean;
  atLimit: boolean;
  /** Called with the chosen/dropped file, or with nothing when the picker should open the modal empty. */
  onFile: (file: File | null) => void;
  onReject: (message: string) => void;
  ctaVariant?: 'primary' | 'dark';
}) {
  const [drag, setDrag] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const accept = (f: File | undefined) => {
    if (!f) return;
    if (!isStudioAudio(f)) onReject(STUDIO_FORMAT_ERROR);
    else onFile(f);
  };
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDrag(false);
    accept(e.dataTransfer.files?.[0]);
  };
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        if (!drag) setDrag(true);
      }}
      onDragLeave={() => setDrag(false)}
      onDrop={onDrop}
      className={cx('rounded-[24px] border-[1.5px] border-dashed text-center', drag ? 'border-acc bg-acc-bg' : 'border-line2 bg-card', empty ? 'px-6 py-14' : 'px-6 py-9')}
      style={{ transition: 'background .2s ease, border-color .2s ease' }}
    >
      <input
        ref={input}
        type="file"
        accept={STUDIO_ACCEPT}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        data-testid="studio-file-input"
        onChange={(e) => {
          accept(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      <div className="w-14 h-14 rounded-[16px] bg-acc-bg2 text-acc flex items-center justify-center mx-auto mb-4" aria-hidden="true">
        <Upload size={26} strokeWidth={1.8} />
      </div>
      <div className="text-[18px] font-semibold">{empty ? 'No songs yet' : 'Drop a rehearsal recording here'}</div>
      <div className="text-[13.5px] text-t4 mt-1.5 mb-5 mx-auto max-w-[380px] leading-normal wp-pretty">
        {empty
          ? "Upload your first rehearsal recording from your computer and we'll detect the chords and key for your team. MP3, WAV or M4A."
          : 'or choose one from your computer · MP3, WAV or M4A · one song per file'}
      </div>
      <Button
        variant={ctaVariant}
        size="lg"
        glow={ctaVariant === 'primary'}
        className="!h-11"
        icon={<Monitor size={16} strokeWidth={2.2} aria-hidden="true" />}
        onClick={() => input.current?.click()}
      >
        Choose file from computer
      </Button>
      {atLimit && <div className="text-[12.5px] text-amb mt-3.5">Your library is full. You&apos;ll choose a song to replace before uploading.</div>}
    </div>
  );
}

/** "In progress" and "Your church's songs" groups; remove asks first. */
export function StudioRows({
  uploads,
  onRemove,
  onRename,
  onRemoved,
}: {
  uploads: ChordUpload[];
  onRemove: (sourceId: string) => Promise<void> | void;
  onRename: (sourceId: string, title: string) => void;
  onRemoved?: (title: string) => void;
}) {
  const [target, setTarget] = useState<ChordUpload | null>(null);
  const [busy, setBusy] = useState(false);
  const inProgress = uploads.filter((u) => u.status === 'pending' || u.status === 'processing');
  const ready = uploads.filter((u) => u.status === 'completed' || u.status === 'failed');
  const ask = (sourceId: string) => setTarget(uploads.find((u) => u.sourceId === sourceId) ?? null);

  return (
    <>
      {inProgress.length > 0 && (
        <section aria-label="In progress">
          <SectionLabel count={inProgress.length} className="mt-7 mb-2.5">
            In progress
          </SectionLabel>
          <div className="rounded-[22px] border border-line bg-card overflow-hidden">
            {inProgress.map((u) => (
              <ChordUploadItem key={u.sourceId} upload={u} onRemove={ask} onRename={onRename} />
            ))}
          </div>
        </section>
      )}
      {ready.length > 0 && (
        <section aria-label="Your church's songs">
          <SectionLabel count={ready.length} className="mt-7 mb-2.5">
            Your church&apos;s songs
          </SectionLabel>
          <div className="rounded-[22px] border border-line bg-card overflow-hidden">
            {ready.map((u) => (
              <ChordUploadItem key={u.sourceId} upload={u} onRemove={ask} onRename={onRename} />
            ))}
          </div>
          <div className="text-[12.5px] text-t4 text-center mt-3.5">
            Tip: record one song per file, close to the band. A key hint like G or Am sharpens detection.
          </div>
        </section>
      )}
      <ConfirmDialog
        isOpen={!!target}
        onClose={() => setTarget(null)}
        busy={busy}
        type="danger"
        closeOnConfirm={false}
        title={target ? `Remove "${target.title}"?` : 'Remove song?'}
        message="The recording, its separated stems and detected chords are permanently deleted. This frees a slot in your library."
        confirmText="Remove"
        onConfirm={async () => {
          if (!target) return;
          setBusy(true);
          try {
            await onRemove(target.sourceId);
            onRemoved?.(target.title);
          } finally {
            setBusy(false);
            setTarget(null);
          }
        }}
      />
    </>
  );
}
